/**
 * The calendar overlay (M10): read-only events from the ICS feeds in the workspace settings, fetched by
 * the server (`GET /api/v1/calendar/events`). Kept in memory per range; refreshed every ten minutes.
 * Offline, the overlay is simply empty — nothing of it lives in the doc.
 */
import { useEffect, useState } from "react";
import { apiCall } from "../core/api";

export interface CalendarEvent {
  calendarId: string;
  calendar: string;
  color: string | null;
  title: string;
  start: string; // ISO timestamp, or a date for all-day events
  end: string;
  allDay: boolean;
  location: string | null;
}

const REFRESH_MS = 10 * 60_000;
const cache = new Map<string, { at: number; events: CalendarEvent[] }>();
const NONE: CalendarEvent[] = [];

export function useCalendarEvents(
  workspaceId: string | null | undefined,
  since: string,
  until: string,
  enabled = true,
): CalendarEvent[] {
  const key = `${workspaceId}|${since}|${until}`;
  const [loaded, setLoaded] = useState<{ key: string; events: CalendarEvent[] } | null>(null);
  useEffect(() => {
    if (!workspaceId || !enabled) return;
    let alive = true;
    const load = () =>
      apiCall<{ events: CalendarEvent[] }>(
        workspaceId,
        "GET",
        `/calendar/events?since=${since}&until=${until}`,
      )
        .then((r) => {
          cache.set(key, { at: Date.now(), events: r.events });
          if (alive) setLoaded({ key, events: r.events });
        })
        .catch(() => undefined);
    const hit = cache.get(key);
    if (!hit || Date.now() - hit.at > REFRESH_MS) void load();
    const timer = window.setInterval(() => void load(), REFRESH_MS);
    return () => {
      alive = false;
      window.clearInterval(timer);
    };
  }, [workspaceId, since, until, key, enabled]);
  if (!enabled) return NONE;
  return loaded?.key === key ? loaded.events : (cache.get(key)?.events ?? NONE);
}

/** Timed events shown in one hour row: those starting in it, with starts before `lo` or after `hi`
 * pulled into the first or last row (like due tasks), so nothing of the day is hidden. */
export function startingAt(
  events: CalendarEvent[],
  day: string,
  hour: number,
  lo: number,
  hi: number,
): CalendarEvent[] {
  return events.filter((e) => {
    if (e.allDay) return false;
    const d = new Date(e.start);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    return key === day && Math.min(hi, Math.max(lo, d.getHours())) === hour;
  });
}

/** All-day events covering the given local day (their `end` date is exclusive, as in ICS). */
export function allDayOn(events: CalendarEvent[], day: string): CalendarEvent[] {
  return events.filter((e) => e.allDay && e.start <= day && day < e.end);
}

export function timeRange(e: CalendarEvent, lang: string): string {
  if (e.allDay) return "";
  const f = new Intl.DateTimeFormat(lang, { hour: "2-digit", minute: "2-digit" });
  return `${f.format(new Date(e.start))}–${f.format(new Date(e.end))}`;
}
